import { defineComponent as _defineComponent } from 'vue'
import { ref, watch } from "vue";
import {
	convertToHumanNumber,
	formatPrice,
	IBar,
	IBars,
	IToken,
	ITrades,
	RESOLUTIONS_MAP,
} from "@/shared";
import {
	CandlestickData,
	CandlestickSeriesOptions,
	IChartApi,
	ISeriesApi,
	SeriesMarker,
	Time,
	WhitespaceData,
	createChart,
} from "lightweight-charts";
import { fetchWalletHistoryTrades } from "@/api/endpoints";
import { useWalletStore } from "@/pinia";

// interfaces
interface IMarks extends Array<SeriesMarker<Time>> {}

// props
interface IProps {
	token: IToken;
	wallet: string;
	latestCandle?: IBar;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'DatafeedLightChart',
  props: {
    token: null,
    wallet: null,
    latestCandle: null
  },
  setup(__props: any) {

const props = __props as {
	token: IToken;
	wallet: string;
	latestCandle?: IBar;
};



// store
const walletStore = useWalletStore();

// data
const chart = ref<IChartApi | null>(null);
const markHistory = ref<ITrades[]>([]);
const resolution = ref<string>(
	JSON.parse(localStorage.getItem("fluxbeam:chart-options"))?.interval || "1"
);
const resolutionsLabel = ref<{ [i: string]: string }>({
	"1": "1m",
	"3": "3m",
	"5": "5m",
	"15": "15m",
	"30": "30m",
	"60": "1h",
	"180": "2h",
	"240": "4h",
	"1D": "D",
	"1W": "W",
	"1M": "M",
});
const mainSeries = ref<ISeriesApi<
	"Candlestick",
	Time,
	CandlestickData<Time> | WhitespaceData<Time>,
	CandlestickSeriesOptions
> | null>(null);
const inspectorChannel = new BroadcastChannel("candle:inspector");

// methods
function init(): void {
	const container = document.getElementById("light-graph");

	if (!container) {
		setTimeout(() => {
			init();
		}, 100);
		return;
	}

	if (chart.value !== null) {
		chart.value.remove();
	}

	chart.value = createChart(container, {
		layout: {
			background: { color: "black" },
			textColor: "#DDD",
		},
		grid: {
			vertLines: { color: "#111" },
			horzLines: { color: "#111" },
		},
		autoSize: true,
	});

	setBars();
}
async function setBars() {
	mainSeries.value = chart.value.addCandlestickSeries({
		upColor: "#26a69a",
		downColor: "#ef5350",
		borderVisible: false,
		wickUpColor: "#26a69a",
		wickDownColor: "#ef5350",
		priceFormat: {
			type: "price",
			minMove: 0.00001,
		},
	});

	// @ts-ignore
	mainSeries.value.setData(await getBars());

	chart.value.timeScale().fitContent();

	chart.value.subscribeClick((param) => {
		if (param.time) {
			const start = Number(param.time.toString());
			const end = start + RESOLUTIONS_MAP[resolution.value];

			inspectorChannel.postMessage({
				wallet: walletStore.address.toString(),
				mint: props.token.mint,
				start,
				end,
			});
		}
	});
}
async function getBars() {
	const count = 329;
	const range = RESOLUTIONS_MAP[resolution.value];
	const params = new URLSearchParams();
	params.set("interval", resolution.value);
	params.set("to", Math.ceil(Date.now() / 1000).toString());
	params.set("from", Math.ceil((Date.now() - range * count) / 1000).toString());
	params.set("count", count.toString());

	const untreatBars = await (
		await fetch(
			`https://data.fluxbeam.xyz/tokens/${
				props.token.mint
			}/candles?${params.toString()}`
		)
	).json();

	if (!untreatBars || untreatBars.length === 0) {
		return [];
	}

	const bars = [] as IBars;

	for (let i = 0; i < untreatBars.length; i++) {
		const { open, close, high, low, volume, count, timestamp } = untreatBars[i];

		if (!timestamp) continue;

		//Cut off any trailing bars that have no volume so they dont interfere with the realtime update
		if (i == untreatBars.length - 1 && volume == 0 && count == 0) continue;

		bars.push({
			time: new Date(timestamp * 1000).getTime(),
			open,
			close,
			high,
			low,
			volume,
			count,
			isBarClosed: true,
			isLastBar: false,
		});
	}

	return bars;
}
async function getMarks(): Promise<IMarks> {
	const to = Math.ceil((Date.now() + 3.154e10 * 6) / 1000);
	const from = Math.ceil((Date.now() - 3.154e10 * 2) / 1000);

	const traders = await fetchWalletHistoryTrades({
		mint: props.token.address,
		wallet: props.wallet,
		from: from,
		to: to,
	});

	if (!traders || traders?.error) return;

	const mm = {};
	for (let i = 0; i < markHistory.value.length; i++) {
		//ts-ignore
		mm[`${markHistory.value[i].time}-${markHistory.value[i].value}`] = true;
	}

	for (let i = 0; i < traders.length; i++) {
		//ts-ignore
		if (mm[`${traders[i].time}-${traders[i].value}`]) continue;
		markHistory.value.push(traders[i]);
	}

	return setMarks(markHistory.value);
}

function setMarks(traders): IMarks {
	const mm = {};
	for (let i = 0; i < markHistory.value.length; i++) {
		//ts-ignore
		mm[`${markHistory.value[i].time}-${markHistory.value[i].value}`] = true;
	}

	for (let i = 0; i < traders.length; i++) {
		//ts-ignore
		if (mm[`${traders[i].time}-${traders[i].value}`]) continue;
		markHistory.value.push(traders[i]);
	}
	return _setMarks(markHistory.value);
}

function _setMarks(history): IMarks {
	const marks = [] as IMarks;

	if (!history) return marks;

	let tradeSizes = [];
	let runningTotal = 0;
	let min = 0;
	let max = 0;

	for (let i = history.length; i > 0; i--) {
		const trade = history[i - 1];
		const currentTotal = runningTotal || trade.value;

		let amountPct = 0;
		let amount = Number(trade.value);

		const sell = trade.inMint === props.token.address.toString();
		if (sell) {
			runningTotal -= amount;
			amountPct =
				i < history.length
					? Math.abs((runningTotal - currentTotal) / currentTotal) * 100
					: 100;
		} else {
			runningTotal += amount;
			amountPct = runningTotal == 0 ? 100 : (trade.value / runningTotal) * 100;
		}

		if (runningTotal < 0) {
			runningTotal = 0;
			amountPct = 100;
		}

		if (min === 0 || amountPct < min) min = amountPct;
		if (max === 0 || amountPct > max) max = amountPct;

		// console.log(`${sell ? 'SELL': 'BUY'} $${trade.value.toFixed(2)} - $${currentTotal.toFixed(2)} -> $${runningTotal.toFixed(2)} -- ${amountPct.toFixed(2)}%`)
		tradeSizes.push(Math.min(100, amountPct));
	}

	for (let i = 0; i < history.length; i++) {
		const trade = history[i];
		const sell = trade.inMint === props.token.address.toString();
		const amount = sell ? trade.inAmount : trade.outAmount;
		const time = new Date(trade.createdAt * 1000);

		console.log(time.toISOString(), {
			year: time.getFullYear(),
			month: time.getMonth() + 1,
			day: time.getDate(),
		});

		marks.push({
			id: `${trade.value}-${trade.createdAt}`,
			position: "inBar",
			time: time.getTime(),
			color: sell ? "red" : "green",
			size: tradeSizes[history.length - 1 - i] / 10,
			shape: "circle",
			text: `${sell ? "SELL" : "BUY"} ${convertToHumanNumber(amount)} ${
				props.token.symbol
			}\n @ ${formatPrice(
				trade.value / (amount / Math.pow(10, props.token.decimals))
			)}\n = $${trade.value.toFixed(2)} (${tradeSizes[
				history.length - 1 - i
			].toFixed(0)}%)\n ON ${new Date(
				trade.createdAt * 1000
			).toLocaleTimeString()}`,
		});
	}

	return marks;
}
// setup
init();

// watch
watch(
	() => props.latestCandle,
	() => {
		if (!mainSeries.value) return;
		mainSeries.value.update(props.latestCandle as any);
	}
);
watch(
	() => resolution.value,
	() => {
		init();

		const localOpts = localStorage.getItem("fluxbeam:chart-options") ?? "{}";

		const opts = {
			...JSON.parse(localOpts),
			interval: resolution.value,
		};

		localStorage.setItem("fluxbeam:chart-options", JSON.stringify(opts));
	}
);

return { __sfc: true,props, walletStore, chart, markHistory, resolution, resolutionsLabel, mainSeries, inspectorChannel, init, setBars, getBars, getMarks, setMarks, _setMarks }
}

})